import { v4 as uuidv4 } from "uuid"
import Button from "react-bootstrap/Button"
import { FiPlus, FiTrash2 } from "react-icons/fi"
import "./index.scss"

import { Title, Container, Text, Number, Select } from "components/Form"
import { CALC_TEMPLATES, getTemplate, validateFormula, validateSpecReferences } from "state_management/lims/calcEngine"
import { SPEC_PARAMETER_KIND } from "state_management/lims/limsSpecs"
import Preview from "./Preview"
import useT from "utils/hooks/useT"

// SpecEditor — edits the QC specification of an Analysis definition (F-B1, extended by SPEC-016):
// a repeatable list of parameters. Each parameter is MEASURED (name, unit, significant digits,
// acceptance criterion — entered directly, as before) or CALCULATED (the analyst enters named raw
// inputs and the calculation engine derives + judges the value). It is a controlled component:
// the parent owns `parameters`/`formula` state and persists them via the limsSpecs slice on save.
//
// For a CALCULATED parameter the editor offers a template picker (CALC_TEMPLATES, which pre-fills
// inputs/formula/criterion), an inputs editor (named inputs with label/unit/source + an aggregate
// flag) and a per-parameter formula field validated against the declared input keys + the
// whitelisted functions (saving an invalid formula is blocked via onValidityChange).
//
// criterionType drives which bound(s) apply:
//   "min"   → lower bound only  (value ≥ min)
//   "max"   → upper bound only  (value ≤ max)
//   "range" → both bounds       (min ≤ value ≤ max)
export default function SpecEditor({
  parameters,
  onParametersChange,
  onValidityChange,
}) {
  const { t } = useT("common")

  const criterionOptions = [
    { id: "min", name: t("limsSpecCriterionMin") },
    { id: "max", name: t("limsSpecCriterionMax") },
    { id: "range", name: t("limsSpecCriterionRange") },
  ]
  const kindOptions = [
    { id: SPEC_PARAMETER_KIND.MEASURED, name: t("limsSpecKindMeasured") },
    { id: SPEC_PARAMETER_KIND.CALCULATED, name: t("limsSpecKindCalculated") },
  ]
  const sourceOptions = [
    { id: "manual", name: t("limsCalcInputSourceManual") },
    { id: "standardPurity", name: t("limsCalcInputSourceStandardPurity") },
    { id: "standardWaterContent", name: t("limsCalcInputSourceStandardWater") },
    { id: "parameter", name: t("limsCalcInputSourceParameter") },
  ]
  // The template picker offers an explicit "No template" option (K21) whose id is the empty
  // string, so the analyst can deselect a template and build a calculated parameter from scratch.
  // Selecting it clears the template-seeded fields (handled in applyTemplate's no-template branch).
  const templateOptions = [
    { id: "", name: t("limsCalcTemplateNone") },
    ...CALC_TEMPLATES.map((tpl) => ({ id: tpl.id, name: t(tpl.labelKey) })),
  ]

  const updateParameter = (idx, patch) => {
    onParametersChange(parameters.map((param, i) => (i === idx ? { ...param, ...patch } : param)))
  }

  const addParameter = () => {
    onParametersChange([
      ...parameters,
      {
        id: uuidv4(),
        name: "",
        unit: "",
        significantDigits: null,
        criterionType: "range",
        min: null,
        max: null,
        kind: SPEC_PARAMETER_KIND.MEASURED,
        inputs: [],
        formula: "",
        aggregate: null,
        templateId: null,
        individualTolerancePercent: null,
      },
    ])
  }

  const removeParameter = (idx) => {
    onParametersChange(parameters.slice(0, idx).concat(parameters.slice(idx + 1)))
  }

  // Switch a parameter between measured and calculated. Switching back to measured drops the calc
  // fields (the limsSpecs normaliser also enforces this on save).
  const setKind = (idx, kind) => {
    if (kind === SPEC_PARAMETER_KIND.CALCULATED) {
      updateParameter(idx, { kind })
    } else {
      updateParameter(idx, {
        kind: SPEC_PARAMETER_KIND.MEASURED,
        inputs: [],
        formula: "",
        aggregate: null,
        templateId: null,
        individualTolerancePercent: null,
      })
    }
  }

  // Apply a template: copy its inputs/formula/criterion/aggregate/tolerance onto the parameter,
  // resolving each input's i18n labelKey to a concrete label so the inputs editor shows text.
  //
  // "No template" (K21): the empty-string id (and any unknown id) clears EVERY template-seeded
  // field — templateId, formula, inputs, aggregate, individualTolerancePercent — leaving a blank
  // calculated parameter the analyst defines from scratch (kind stays calculated; the criterion
  // fields are left as-is so a deselect doesn't silently rewrite the analyst's bounds).
  //
  // ROOT-CAUSE FIX (the "template seeded the formula but the Inputs list stayed empty" bug): a
  // template is intrinsically a CALCULATED-parameter concept, so applying one must guarantee
  // `kind: CALCULATED`. Without this, a parameter could carry a template formula while kind was
  // still "measured"; normalizeParameter (limsSpecs) keeps the formula on a measured parameter
  // but WIPES its inputs — yielding a formula like "ph_reading" with no inputs and the
  // "Unknown name(s): ph_reading" validation error. Setting kind here keeps the two in lock-step.
  const applyTemplate = (idx, templateId) => {
    const tpl = getTemplate(templateId)
    if (!tpl) {
      updateParameter(idx, {
        kind: SPEC_PARAMETER_KIND.CALCULATED,
        templateId: null,
        formula: "",
        inputs: [],
        aggregate: null,
        individualTolerancePercent: null,
      })
      return
    }
    updateParameter(idx, {
      kind: SPEC_PARAMETER_KIND.CALCULATED,
      templateId: tpl.id,
      unit: tpl.unit,
      significantDigits: tpl.significantDigits,
      criterionType: tpl.criterionType,
      min: tpl.min,
      max: tpl.max,
      formula: tpl.formula,
      aggregate: tpl.aggregate ? { ...tpl.aggregate } : null,
      individualTolerancePercent: tpl.individualTolerancePercent,
      inputs: tpl.inputs.map((inp) => ({
        key: inp.key,
        label: inp.labelKey ? t(inp.labelKey) : inp.label || "",
        unit: inp.unit || "",
        source: inp.source || "manual",
      })),
    })
  }

  // ---- per-parameter input (named variable) editing ----
  const updateInput = (paramIdx, inputIdx, patch) => {
    const param = parameters[paramIdx]
    const inputs = (param.inputs || []).map((inp, i) => (i === inputIdx ? { ...inp, ...patch } : inp))
    updateParameter(paramIdx, { inputs })
  }
  const addInput = (paramIdx) => {
    const param = parameters[paramIdx]
    updateParameter(paramIdx, {
      inputs: [...(param.inputs || []), { key: "", label: "", unit: "", source: "manual" }],
    })
  }
  const removeInput = (paramIdx, inputIdx) => {
    const param = parameters[paramIdx]
    const removedKey = (param.inputs || [])[inputIdx] && param.inputs[inputIdx].key
    const inputs = (param.inputs || []).filter((_inp, i) => i !== inputIdx)
    // Clear the aggregate flag if it pointed at the removed input.
    const aggregate = param.aggregate && param.aggregate.inputKey === removedKey ? null : param.aggregate
    updateParameter(paramIdx, { inputs, aggregate })
  }

  // SPEC-023: change an input's source. Switching TO "parameter" awaits the parameter pick
  // (parameterId null — the reference validation blocks saving until one is chosen); switching
  // AWAY drops the reference id. A parameter-bound input is a resolved scalar and can never be
  // the aggregate n-value list, so a stale aggregate flag pointing at it is cleared — in the
  // SAME parameters update (two sequential updateInput/updateParameter calls would clobber each
  // other: both map over the still-unchanged `parameters` prop of this controlled component).
  const setInputSource = (paramIdx, inputIdx, source) => {
    onParametersChange(
      parameters.map((param, i) => {
        if (i !== paramIdx) return param
        const changed = (param.inputs || [])[inputIdx]
        const inputs = (param.inputs || []).map((inp, j) =>
          j === inputIdx ? { ...inp, source, parameterId: null } : inp
        )
        const aggregate =
          source === "parameter" && changed && param.aggregate && param.aggregate.inputKey === changed.key
            ? null
            : param.aggregate
        return { ...param, inputs, aggregate }
      })
    )
  }

  // SPEC-023: bind one calculated input to ANOTHER parameter of the spec. The reference is by
  // STABLE parameterId (names may change; ids never do); the engine variable key is DERIVED from
  // the chosen parameter's name (unique within this parameter's inputs), and label/unit default
  // to the source parameter's so the result panel shows a meaningful read-only row.
  const pickSourceParameter = (paramIdx, inputIdx, sourceParameterId) => {
    const param = parameters[paramIdx]
    const source = parameters.find((p) => p.id === sourceParameterId)
    if (!param || !source) return
    const otherKeys = (param.inputs || []).filter((_inp, i) => i !== inputIdx).map((inp) => inp.key)
    updateInput(paramIdx, inputIdx, {
      source: "parameter",
      parameterId: source.id,
      key: deriveInputKey(source.name, otherKeys),
      label: source.name || "",
      unit: source.unit || "",
    })
  }
  // The aggregate flag is exclusive: at most one input is the aggregate (n-value list) source.
  const setAggregateInput = (paramIdx, inputKey, on) => {
    updateParameter(paramIdx, { aggregate: on ? { inputKey } : null })
  }

  // Validate every calculated parameter's formula against its declared input keys + the
  // whitelisted functions; collect the first error per parameter. An empty formula on a
  // calculated parameter is also invalid (nothing to compute).
  const formulaErrors = {}
  parameters.forEach((param) => {
    if (param.kind !== SPEC_PARAMETER_KIND.CALCULATED) return
    const keys = (param.inputs || []).map((inp) => inp.key).filter(Boolean)
    const res = validateFormula(param.formula || "", keys)
    if (!res.ok) formulaErrors[param.id] = res.error
  })
  // SPEC-023: live structural validation of cross-parameter references — an unknown/unpicked
  // reference, a self-reference or a cycle blocks saving exactly like an invalid formula, and
  // the message names the chain ("Circular reference: A → B → A"). Same contract the backend
  // enforces with a 422 at definition save.
  const referenceValidation = validateSpecReferences(parameters)
  const referenceErrors = referenceValidation.ok ? {} : referenceValidation.errors
  // Surface overall validity to the parent so it can block saving an invalid spec.
  const isValid = Object.keys(formulaErrors).length === 0 && referenceValidation.ok
  if (onValidityChange) onValidityChange(isValid)

  return (
    <div className="SpecEditor">
      <div className="SpecEditor-parameters">
        {parameters.length === 0 && <p className="SpecEditor-empty">{t("limsSpecNoParameters")}</p>}
        {parameters.map((param, idx) => {
          const calculated = param.kind === SPEC_PARAMETER_KIND.CALCULATED
          const showMin = param.criterionType === "min" || param.criterionType === "range"
          const showMax = param.criterionType === "max" || param.criterionType === "range"
          return (
            <div className="SpecEditor-row" key={param.id}>
              <div className="SpecEditor-name">
                <Container marginBottom="">
                  <Title>{t("limsSpecParameterName")}</Title>
                  <Text value={param.name} onChange={(value) => updateParameter(idx, { name: value })} />
                </Container>
              </div>
              <div className="SpecEditor-kind">
                <Container marginBottom="">
                  <Title>{t("limsSpecKind")}</Title>
                  <Select
                    isMulti={false}
                    collection={kindOptions}
                    value={param.kind || SPEC_PARAMETER_KIND.MEASURED}
                    onChange={(values) => setKind(idx, values[0] || SPEC_PARAMETER_KIND.MEASURED)}
                    menuPlacement="bottom"
                  />
                </Container>
              </div>
              <div className="SpecEditor-unit">
                <Container marginBottom="">
                  <Title>{t("limsSpecUnit")}</Title>
                  <Text value={param.unit} onChange={(value) => updateParameter(idx, { unit: value })} />
                </Container>
              </div>
              <div className="SpecEditor-digits">
                <Container marginBottom="">
                  <Title>{t("limsSpecSignificantDigits")}</Title>
                  <Number
                    min={0}
                    value={param.significantDigits}
                    onChange={(value) => updateParameter(idx, { significantDigits: value })}
                  />
                </Container>
              </div>
              {/* The acceptance criterion and its bounds are ONE thought, and the criterion's select
                  carries the longest labels on the row. Given its own full-width line it stops being
                  squeezed into whatever the identity fields leave over, which is what truncated it. */}
              <div className="SpecEditor-row-criterion">
              <div className="SpecEditor-criterion">
                <Container marginBottom="">
                  <Title>{t("limsSpecCriterionType")}</Title>
                  <Select
                    isMulti={false}
                    collection={criterionOptions}
                    value={param.criterionType}
                    onChange={(values) => updateParameter(idx, { criterionType: values[0] || "range" })}
                    menuPlacement="bottom"
                  />
                </Container>
              </div>
              {showMin && (
                <div className="SpecEditor-bound">
                  <Container marginBottom="">
                    <Title>{t("limsSpecMin")}</Title>
                    <Text
                      value={boundText(param.min)}
                      onChange={(value) => updateParameter(idx, { min: value === "" ? null : value })}
                    />
                  </Container>
                </div>
              )}
              {showMax && (
                <div className="SpecEditor-bound">
                  <Container marginBottom="">
                    <Title>{t("limsSpecMax")}</Title>
                    <Text
                      value={boundText(param.max)}
                      onChange={(value) => updateParameter(idx, { max: value === "" ? null : value })}
                    />
                  </Container>
                </div>
              )}
              <Button
                variant="danger"
                size="sm"
                className="SpecEditor-remove"
                title={t("limsSpecRemoveParameter")}
                onClick={() => removeParameter(idx)}
              >
                <FiTrash2 />
              </Button>
              </div>

              {calculated && (
                <CalculatedEditor
                  param={param}
                  idx={idx}
                  t={t}
                  parameters={parameters}
                  templateOptions={templateOptions}
                  sourceOptions={sourceOptions}
                  formulaError={formulaErrors[param.id]}
                  referenceError={referenceErrors[param.id]}
                  applyTemplate={applyTemplate}
                  updateParameter={updateParameter}
                  updateInput={updateInput}
                  addInput={addInput}
                  removeInput={removeInput}
                  setAggregateInput={setAggregateInput}
                  setInputSource={setInputSource}
                  pickSourceParameter={pickSourceParameter}
                />
              )}
            </div>
          )
        })}
      </div>

      <Button variant="primary" size="sm" className="SpecEditor-add" onClick={addParameter}>
        <FiPlus /> {t("limsSpecAddParameter")}
      </Button>

      {/* K23: live preview of the analyst's result-entry screen for this spec. Purely local
          (nothing persisted); reuses the same judge logic the real panel + server share. */}
      <Preview parameters={parameters} />
    </div>
  )
}

// The calculated-parameter sub-editor: template picker + named-inputs editor + per-parameter
// formula (with live validation error). Rendered only when the parameter's kind is calculated.
function CalculatedEditor({
  param,
  idx,
  t,
  parameters,
  templateOptions,
  sourceOptions,
  formulaError,
  referenceError,
  applyTemplate,
  updateParameter,
  updateInput,
  addInput,
  removeInput,
  setAggregateInput,
  setInputSource,
  pickSourceParameter,
}) {
  const inputs = param.inputs || []
  const aggregateKey = param.aggregate ? param.aggregate.inputKey : null
  // SPEC-023: the OTHER parameters of the spec a "parameter"-sourced input may reference
  // (measured AND calculated — chains are allowed; cycles are caught by the live validation).
  const referenceOptions = (parameters || [])
    .filter((p) => p.id !== param.id)
    .map((p) => ({ id: p.id, name: p.name || t("limsSpecPreviewUnnamed") }))
  return (
    <div className="SpecEditor-calc">
      <div className="SpecEditor-calc-template">
        <Container marginBottom="">
          <Title>{t("limsCalcTemplate")}</Title>
          <Select
            isMulti={false}
            collection={templateOptions}
            value={param.templateId || ""}
            onChange={(values) => applyTemplate(idx, values[0] || "")}
            menuPlacement="bottom"
          />
        </Container>
        <p className="SpecEditor-calc-help">{t("limsCalcTemplateHelp")}</p>
      </div>

      <div className="SpecEditor-calc-inputs">
        <Title>{t("limsCalcInputs")}</Title>
        {inputs.length === 0 && <p className="SpecEditor-empty">{t("limsCalcNoInputs")}</p>}
        {inputs.map((inp, inputIdx) => (
          <div className="SpecEditor-calc-input" key={`${param.id}-input-${inputIdx}`}>
            {inp.source === "parameter" ? (
              // SPEC-023: a parameter-sourced input is bound by picking the source parameter —
              // the engine key is DERIVED from its name (shown as a hint below the picker, for
              // use in the formula), so the free-text Key field is swapped out.
              <Container marginBottom="">
                <Title>{t("limsCalcInputSourceParameterPick")}</Title>
                <Select
                  isMulti={false}
                  collection={referenceOptions}
                  value={inp.parameterId || ""}
                  onChange={(values) => pickSourceParameter(idx, inputIdx, values[0] || null)}
                  menuPlacement="bottom"
                />
                {inp.key && <p className="SpecEditor-calc-help">{inp.key}</p>}
              </Container>
            ) : (
              <Container marginBottom="">
                <Title>{t("limsCalcInputKey")}</Title>
                <Text value={inp.key} onChange={(v) => updateInput(idx, inputIdx, { key: v })} />
              </Container>
            )}
            <Container marginBottom="">
              <Title>{t("limsCalcInputLabel")}</Title>
              <Text value={inp.label} onChange={(v) => updateInput(idx, inputIdx, { label: v })} />
            </Container>
            <Container marginBottom="">
              <Title>{t("limsCalcInputUnit")}</Title>
              <Text value={inp.unit} onChange={(v) => updateInput(idx, inputIdx, { unit: v })} />
            </Container>
            <div className="SpecEditor-calc-source">
              <Container marginBottom="">
                <Title>{t("limsCalcInputSource")}</Title>
                <Select
                  isMulti={false}
                  collection={sourceOptions}
                  value={inp.source || "manual"}
                  onChange={(values) => setInputSource(idx, inputIdx, values[0] || "manual")}
                  menuPlacement="bottom"
                />
              </Container>
            </div>
            <label className="SpecEditor-calc-aggregate" title={t("limsCalcAggregateHelp")}>
              <input
                type="checkbox"
                checked={aggregateKey === inp.key && !!inp.key}
                disabled={!inp.key || inp.source === "parameter"}
                onChange={(e) => setAggregateInput(idx, inp.key, e.target.checked)}
              />
              <span>{t("limsCalcAggregate")}</span>
            </label>
            <Button
              variant="danger"
              size="sm"
              className="SpecEditor-remove"
              title={t("limsCalcInputRemove")}
              onClick={() => removeInput(idx, inputIdx)}
            >
              <FiTrash2 />
            </Button>
          </div>
        ))}
        <Button variant="light" size="sm" className="SpecEditor-calc-add-input" onClick={() => addInput(idx)}>
          <FiPlus /> {t("limsCalcInputAdd")}
        </Button>
      </div>

      {aggregateKey && (
        <Container marginBottom="">
          <Title>{t("limsCalcIndividualTolerance")}</Title>
          <Text
            value={boundText(param.individualTolerancePercent)}
            onChange={(v) => updateParameter(idx, { individualTolerancePercent: v === "" ? null : v })}
          />
        </Container>
      )}

      <div className="SpecEditor-calc-formula">
        <Title>{t("limsCalcFormula")}</Title>
        <Text
          large
          rows={2}
          placeholder={t("limsCalcFormulaPlaceholder")}
          value={param.formula || ""}
          onChange={(v) => updateParameter(idx, { formula: v })}
        />
        {formulaError && (
          <p className="SpecEditor-calc-formula-error">
            {t("limsCalcFormulaInvalid")}: {formulaError}
          </p>
        )}
        {/* SPEC-023: structural reference errors (unknown reference / cycle) render exactly like
            formula errors — the engine message names the chain, e.g. "Circular reference: A → B
            → A" — and block saving via the same validity gate. */}
        {referenceError && (
          <p className="SpecEditor-calc-formula-error">
            {t("limsCalcReferenceInvalid")}: {referenceError}
          </p>
        )}
        {!formulaError && !referenceError && (
          <p className="SpecEditor-calc-help">{t("limsCalcFormulaHelp")}</p>
        )}
      </div>
    </div>
  )
}

// Render a min/max bound for its text input. Bounds are kept as the raw strings the analyst
// typed (so partial/decimal input is never clobbered). Anything non-primitive — e.g. stale or
// corrupt data persisted by an earlier build — renders as empty, so the field stays usable and
// the analyst can simply type the value afresh (it then persists clean).
function boundText(v) {
  if (v === null || v === undefined || typeof v === "object") return ""
  return String(v)
}

// SPEC-023: derive an engine-safe variable key from a parameter name — lowercase, runs of
// characters outside [a-z0-9_] become "_" (the engine tokenizer only accepts ASCII identifiers),
// and a leading digit gets a "p_" prefix (identifiers must start with a letter or "_"). A blank
// name yields "p". Uniqueness within the parameter's input list is ensured with a numeric
// suffix, so two references to same-named parameters never collide in the variable map.
function deriveInputKey(name, takenKeys) {
  // Char-wise mapping (no regex — deterministic and immune to backtracking concerns): keep
  // [a-z0-9_] of the lowercased name, map every other character to "_", collapsing runs.
  let base = ""
  for (const ch of String(name || "").toLowerCase()) {
    const keep = (ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9") || ch === "_"
    if (keep && ch !== "_") base += ch
    else if (!base.endsWith("_") && base !== "") base += "_"
  }
  while (base.endsWith("_")) base = base.slice(0, -1)
  if (base === "") base = "p"
  if (base[0] >= "0" && base[0] <= "9") base = `p_${base}`
  let key = base
  let n = 2
  while (takenKeys.includes(key)) {
    key = `${base}_${n}`
    n += 1
  }
  return key
}
